import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { format, parse, startOfDay, subDays } from "date-fns";
import { Filter, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";

import { EpisodeCard } from "@/components/explore/episode-card";
import { usePlayer } from "@/components/providers/player-provider";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/useismobile";
import {
  episodesByIdsQuery,
  episodesInfiniteQuery,
  publicTagsQuery,
  seedEpisodes,
} from "@/lib/queries";
import type { EpisodeFilters } from "@/lib/queries";
import { useArchiveSearch } from "@/lib/use-archive-search";
import { cn } from "@/lib/utils";

const useTypeToSearch = (input: React.RefObject<HTMLInputElement | null>) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable=true]")) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (event.key.length === 1) {
        input.current?.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [input]);
};

interface ExploreFiltersProps {
  activeFilterCount: number;
  allTags: string[];
  fromDate: string;
  onClear: () => void;
  onFromDateChange: (value: string) => void;
  onTagToggle: (tag: string, checked: boolean) => void;
  onToDateChange: (value: string) => void;
  selectedTags: string[];
  toDate: string;
}

const DATE_FORMAT = "yyyy-MM-dd";

const parseDay = (value: string) =>
  value ? parse(value, DATE_FORMAT, new Date()) : undefined;

const datePresets = [
  { days: 7, label: "Past week" },
  { days: 30, label: "Past month" },
  { days: 90, label: "Past 3 months" },
  { days: 365, label: "Past year" },
] as const;

const describeRange = (from: Date | undefined, to: Date | undefined) => {
  if (from && to) {
    return `${format(from, "d MMM yyyy")} – ${format(to, "d MMM yyyy")}`;
  }
  if (from) {
    return `From ${format(from, "d MMM yyyy")}`;
  }
  if (to) {
    return `Until ${format(to, "d MMM yyyy")}`;
  }
  return "Any date";
};

const calendarClassNames = {
  day_button:
    "data-[range-middle=true]:bg-primary/10 data-[range-middle=true]:hover:bg-primary/15",
  outside: "text-muted-foreground/50 aria-selected:text-muted-foreground",
  range_end:
    "relative isolate z-0 after:absolute after:inset-y-0 after:left-0 after:w-1/2 after:bg-primary/10",
  range_start:
    "relative isolate z-0 after:absolute after:inset-y-0 after:right-0 after:w-1/2 after:bg-primary/10",
  root: "w-full",
  today: "font-semibold text-primary data-[selected=true]:rounded-none",
};

const DateRangeFilter = (props: {
  fromDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  toDate: string;
}) => {
  const from = parseDay(props.fromDate);
  const to = parseDay(props.toDate);
  const today = startOfDay(new Date());

  const setRange = (range: DateRange | undefined) => {
    props.onFromDateChange(range?.from ? format(range.from, DATE_FORMAT) : "");
    props.onToDateChange(range?.to ? format(range.to, DATE_FORMAT) : "");
  };

  const activePreset = datePresets.find(
    (preset) =>
      props.toDate === format(today, DATE_FORMAT) &&
      props.fromDate === format(subDays(today, preset.days), DATE_FORMAT)
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium">Date</p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {describeRange(from, to)}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {datePresets.map((preset) => (
          <Button
            aria-pressed={activePreset === preset}
            className="w-full"
            key={preset.days}
            onClick={() =>
              setRange({ from: subDays(today, preset.days), to: today })
            }
            size="sm"
            variant={activePreset === preset ? "default" : "outline"}
          >
            {preset.label}
          </Button>
        ))}
      </div>
      <Calendar
        className="w-full bg-transparent p-0 [--cell-size:--spacing(9)]"
        classNames={calendarClassNames}
        defaultMonth={to ?? from ?? today}
        mode="range"
        onSelect={setRange}
        selected={{ from, to }}
        weekStartsOn={1}
      />
    </div>
  );
};

const filterBody = (props: ExploreFiltersProps) => (
  <div className="flex flex-col gap-4">
    {props.allTags.length > 0 && (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Tags</p>
        <div className="flex max-h-40 flex-col gap-2 overflow-y-auto">
          {props.allTags.map((tag) => (
            <label
              className="flex cursor-pointer items-center gap-2 text-sm"
              key={tag}
            >
              <Checkbox
                checked={props.selectedTags.includes(tag)}
                onCheckedChange={(checked) =>
                  props.onTagToggle(tag, checked === true)
                }
              />
              {tag}
            </label>
          ))}
        </div>
      </div>
    )}

    <DateRangeFilter
      fromDate={props.fromDate}
      onFromDateChange={props.onFromDateChange}
      onToDateChange={props.onToDateChange}
      toDate={props.toDate}
    />

    <Button
      disabled={props.activeFilterCount === 0}
      onClick={props.onClear}
      variant="ghost"
    >
      Clear filters
    </Button>
  </div>
);

const filterTrigger = (props: ExploreFiltersProps) => (
  <Button variant={props.activeFilterCount > 0 ? "default" : "outline"}>
    <Filter />
    Filter
    {props.activeFilterCount > 0 && ` (${props.activeFilterCount})`}
  </Button>
);

const ExploreFilters = (props: ExploreFiltersProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger render={filterTrigger(props)} />
        <SheetContent
          className="max-h-[85dvh] gap-4 overflow-y-auto px-6 pb-8 pt-2"
          side="bottom"
        >
          <SheetTitle>Filter</SheetTitle>
          {filterBody(props)}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger render={filterTrigger(props)} />
      <PopoverContent align="end" className="w-80">
        {filterBody(props)}
      </PopoverContent>
    </Popover>
  );
};

interface ResultsStatusProps {
  activeFilterCount: number;
  episodesCount: number;
  isFetchingNextPage: boolean;
  isPending: boolean;
  matching: boolean;
  query: string;
  searchLoading: boolean;
  searching: boolean;
  shownCount: number;
}

const ResultsStatus = (props: ResultsStatusProps) => (
  <>
    {props.searching &&
      (props.searchLoading || props.matching) &&
      props.shownCount === 0 && (
        <p className="px-4 py-16 text-center text-muted-foreground">
          Searching…
        </p>
      )}

    {props.searching &&
      !props.searchLoading &&
      !props.matching &&
      props.shownCount === 0 && (
        <p className="px-4 py-16 text-center text-muted-foreground">
          Nothing matches “{props.query}”.
        </p>
      )}

    {!(props.searching || props.isPending) && props.episodesCount === 0 && (
      <p className="px-4 py-16 text-center text-muted-foreground">
        {props.activeFilterCount > 0
          ? "No episodes match these filters."
          : "No episodes to show."}
      </p>
    )}

    {!props.searching && props.isFetchingNextPage && (
      <p className="px-4 py-8 text-center text-sm text-muted-foreground">
        Loading more…
      </p>
    )}
  </>
);

export function ExplorePage(props: {
  initialFromDate?: string;
  initialToDate?: string;
}) {
  const [query, setQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState(props.initialFromDate ?? "");
  const [toDate, setToDate] = useState(props.initialToDate ?? "");
  const queryClient = useQueryClient();
  const sentinel = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const { current: nowPlaying } = usePlayer();

  const filters = useMemo<EpisodeFilters>(() => {
    const active: EpisodeFilters = {};
    if (fromDate) {
      active.from = `${fromDate}T00:00:00.000Z`;
    }
    if (toDate) {
      active.to = `${toDate}T23:59:59.999Z`;
    }
    if (selectedTags.length > 0) {
      active.tags = selectedTags;
    }
    return active;
  }, [fromDate, selectedTags, toDate]);

  const activeFilterCount =
    selectedTags.length + (fromDate ? 1 : 0) + (toDate ? 1 : 0);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isPlaceholderData,
  } = useInfiniteQuery(
    episodesInfiniteQuery(undefined, 30, undefined, filters)
  );

  const { data: allTags } = useQuery(publicTagsQuery());

  const total = data?.pages[0]?.total ?? 0;

  const episodes = useMemo(
    () => data?.pages.flatMap((page) => page.episodes) ?? [],
    [data]
  );

  useEffect(() => {
    seedEpisodes(queryClient, episodes);
  }, [episodes, queryClient]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage]);

  useTypeToSearch(searchInput);

  const normalised = query.trim();
  const search = useArchiveSearch(query);

  const searching = normalised.length > 0;

  const handleSearchFocus = () => {
    search.prepare();
  };

  const matchIds = useMemo(
    () => search.results.slice(0, 48).map((result) => result.id),
    [search.results]
  );
  const { data: matched, isFetching: matching } = useQuery(
    episodesByIdsQuery(searching ? matchIds : [])
  );

  const shown = searching ? (matched ?? []) : episodes;

  const clearFilters = () => {
    setSelectedTags([]);
    setFromDate("");
    setToDate("");
  };

  const toggleTag = (tag: string, checked: boolean) => {
    setSelectedTags((current) =>
      checked
        ? [...current, tag].toSorted((a, b) => a.localeCompare(b))
        : current.filter((name) => name !== tag)
    );
  };

  const resultsLabel = searching
    ? `${shown.length} ${shown.length === 1 ? "result" : "results"}`
    : `${total} ${total === 1 ? "result" : "results"}`;

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-7xl px-6 py-10 lg:px-10",
        nowPlaying && "pb-24"
      )}
    >
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-extrabold tracking-tight">Explore</h1>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="relative w-full sm:w-80">
            <Search className="absolute top-1/2 start-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="ps-9"
              placeholder="Search shows, episodes or hosts..."
              value={query}
              ref={searchInput}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={handleSearchFocus}
            />
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm whitespace-nowrap text-muted-foreground">
              {resultsLabel}
            </span>
            <ExploreFilters
              activeFilterCount={activeFilterCount}
              allTags={allTags ?? []}
              fromDate={fromDate}
              onClear={clearFilters}
              onFromDateChange={setFromDate}
              onTagToggle={toggleTag}
              onToDateChange={setToDate}
              selectedTags={selectedTags}
              toDate={toDate}
            />
          </div>
        </div>
      </div>

      <div
        className={cn(
          "mt-8 grid grid-cols-2 gap-4 transition-opacity sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6",
          isPlaceholderData && "opacity-60"
        )}
      >
        {shown.map((episode, index) => (
          <EpisodeCard
            episode={episode}
            key={episode.id}
            priority={index < 6}
          />
        ))}
      </div>

      {!searching && <div aria-hidden className="h-px" ref={sentinel} />}

      <ResultsStatus
        activeFilterCount={activeFilterCount}
        episodesCount={episodes.length}
        isFetchingNextPage={isFetchingNextPage}
        isPending={isPending}
        matching={matching}
        query={normalised}
        searchLoading={search.loading}
        searching={searching}
        shownCount={shown.length}
      />
    </div>
  );
}
